import { percent, type AppearanceColor } from './appearance.util';

/** The field's right edge: hues run 0 to 359 across it. */
export const MAX_HUE = 359;

/**
 * The value a key gives a 0 to 100 slider: arrows move 1 (Shift 10),
 * PageUp and PageDown 10, Home and End go to the ends. Null for any other key.
 */
export function sliderKeyValue(
  key: string,
  shiftKey: boolean,
  value: number,
  min = 0,
  max = 100
): number | null {
  const step = shiftKey ? 10 : 1;
  let next: number;
  switch (key) {
    case 'ArrowRight':
    case 'ArrowUp':
      next = value + step;
      break;
    case 'ArrowLeft':
    case 'ArrowDown':
      next = value - step;
      break;
    case 'PageUp':
      next = value + 10;
      break;
    case 'PageDown':
      next = value - 10;
      break;
    case 'Home':
      next = min;
      break;
    case 'End':
      next = max;
      break;
    default:
      return null;
  }
  return Math.min(max, Math.max(min, next));
}

/** Keys a slider takes; their release saves what they previewed. */
export const SLIDER_KEYS = new Set([
  'ArrowRight',
  'ArrowUp',
  'ArrowLeft',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
]);

/** A colour moved by a key on its handle, or null for a key the handle does not take. */
export function handleKeyColor(
  color: AppearanceColor,
  key: string,
  shiftKey: boolean
): AppearanceColor | null {
  const step = shiftKey ? 10 : 1;
  const hue = (value: number) => Math.min(MAX_HUE, Math.max(0, value));
  switch (key) {
    case 'ArrowLeft':
      return { ...color, hue: hue(color.hue - step) };
    case 'ArrowRight':
      return { ...color, hue: hue(color.hue + step) };
    case 'ArrowUp':
      return { ...color, tone: percent(color.tone + step) };
    case 'ArrowDown':
      return { ...color, tone: percent(color.tone - step) };
    case 'PageUp':
      return { ...color, tone: percent(color.tone + 10) };
    case 'PageDown':
      return { ...color, tone: percent(color.tone - 10) };
    case 'Home':
      return { ...color, hue: 0 };
    case 'End':
      return { ...color, hue: MAX_HUE };
    default:
      return null;
  }
}

/** The colour at a point of the field's box: hue across, tone up. */
export function fieldColorAt(
  rect: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number
): AppearanceColor {
  const x = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
  const y = rect.height > 0 ? (clientY - rect.top) / rect.height : 0;
  const clamp = (value: number) => Math.min(1, Math.max(0, value));
  return {
    hue: Math.round(clamp(x) * MAX_HUE),
    tone: Math.round((1 - clamp(y)) * 100),
  };
}

/** The intensity wave's height either side of its middle: more vivid, wavier. */
export const waveAmplitude = (intensity: number) => 1 + (5 * intensity) / 100;

/** The grain dial turns through this many degrees, centred on the top. */
export const DIAL_SWEEP = 270;
const HALF_SWEEP = DIAL_SWEEP / 2;

/** The dial's angle for a value: 0 at the lower left, 100 at the lower right. */
export const dialAngle = (value: number) =>
  -HALF_SWEEP + (DIAL_SWEEP * value) / 100;

/**
 * The dial's value at a point round its centre (0 up, clockwise), clamped to
 * the sweep: the gap at the bottom goes to the nearer end.
 */
export function dialValue(dx: number, dy: number): number {
  const angle = (Math.atan2(dx, -dy) * 180) / Math.PI;
  const clamped = Math.min(HALF_SWEEP, Math.max(-HALF_SWEEP, angle));
  return ((clamped + HALF_SWEEP) / DIAL_SWEEP) * 100;
}
